import { useEffect, useRef, useState } from "react";
import load from "load-script";
import { newrelic } from "@jti/fe-clients";
import { selectOrderNumber, setIsPgsClientPending, useDispatch, useSelector } from "@jti/store";
import { useConfig, useTranslations } from "@jti/ui";
import { useAemModel } from "../../contexts/AemModelContext";
import { useFailedPaymentFlow } from "../../hooks";
import { useFreeTrial } from "../../hooks/useFreeTrial";
import { getPgsCallbackUrls } from "./getPgsCallbackUrls";
export default function usePgsClient({ ref, paymentCode, onUnmount, buttonClassName, orderId }) {
    const dispatch = useDispatch();
    const { __ } = useTranslations();
    const config = useConfig();
    const prevPaymentCode = useRef();
    const { isFailedPaymentFlow } = useFailedPaymentFlow();
    const { failedPaymentFlow } = useAemModel();
    const { isFreeTrial } = useFreeTrial();
    const orderEntityId = useSelector(selectOrderNumber(isFreeTrial));
    const paymentToken = useSelector((state) => state.checkout.paymentToken);
    const pgsAdapter = useSelector((state) => state.checkout.pgsAdapter);
    const [loadedClient, setLoadedClient] = useState(null);
    const unmountList = {};
    const retryPaymentButtonLabel = failedPaymentFlow?.retryPaymentButtonLabel || __("cart.order.payment.pay");
    useEffect(() => {
        if (prevPaymentCode.current !== paymentCode) {
            prevPaymentCode.current = paymentCode;
        }
    }, [paymentCode]);
    useEffect(() => {
        if (!orderEntityId || !pgsAdapter?.client?.length || !orderId) {
            return;
        }
        const { paymentSuccessfulUrl, paymentFailedUrl } = getPgsCallbackUrls({
            orderId,
            paymentToken,
            isFreeTrial,
        });
        const onClientLoad = () => {
            dispatch(setIsPgsClientPending(false));
            const PGS_CLIENT = window.__PGSClient;
            if (PGS_CLIENT) {
                const client = new PGS_CLIENT({
                    tenantToken: config.PGS_X_DES_AK_TENANT_TOKEN,
                    paymentToken,
                    paymentMethod: pgsAdapter.paymentCode,
                    pgsUrl: config.PGS_URL,
                    successUrl: paymentSuccessfulUrl,
                    cancelUrl: paymentFailedUrl,
                    buttonName: isFailedPaymentFlow ? retryPaymentButtonLabel : __("cart.order.payment.pay"),
                    buttonClassName,
                });
                const reference = ref;
                if (reference?.current) {
                    unmountList[pgsAdapter.paymentCode] = client.mount(reference.current);
                }
            }
        };
        if (pgsAdapter.client !== loadedClient) {
            load(pgsAdapter.client, (error) => {
                if (error === null) {
                    setLoadedClient(pgsAdapter.client);
                    onClientLoad();
                    return;
                }
                // TODO: handle PGS adapter script loading error
                console.error("Failed to load PGS ADAPTER SCRIPT!", error);
                newrelic.noticeError(new Error(`Failed to load PGS ADAPTER SCRIPT! ${error}`));
            });
        }
        else {
            onClientLoad();
        }
        return () => {
            prevPaymentCode.current && unmountList[prevPaymentCode.current] && unmountList[prevPaymentCode.current]();
            onUnmount();
        };
    }, [
        pgsAdapter,
        paymentToken,
        ref,
        onUnmount,
        buttonClassName,
        orderEntityId,
        prevPaymentCode,
        retryPaymentButtonLabel,
    ]);
}
//# sourceMappingURL=usePgsClient.js.map